import { useInputValidation } from '@e2m-customer-portal/hooks';
import { CSSClasses, isDefined, Validation } from '@e2m-customer-portal/shared';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select as MuiSelect, SelectProps } from '@mui/material';
import { ComponentType, ReactElement, ReactNode, useId, useState } from 'react';
import { DefaultItemRender } from './DefaultItemRender';
import { DefaultSelectRender } from './DefaultSelectRender';
import { getId, SelectItemType } from './Option';

type OnChangeFunc<T> = (values: T[]) => void;

interface CommonMultiSelectProps<T> {
  /**
   * The text of the label.
   */
  label?: string;

  /**
   * Will get triggered by selecting values with all selected values.
   */
  onChange?: OnChangeFunc<T>;

  /**
   * List of all selectable options.
   */
  options: readonly T[];

  /**
   * The selected values.
   */
  value: T[];

  /**
   * Can be used to overwrite how the items in the select dropdown are rendered.
   * @param item
   * @param selected
   */
  ItemComponent?: ComponentType<{ value: T; selected: boolean }>;

  /**
   * Can be used to overwrite how the select is rendered.
   * @param options
   */
  SelectComponent?: ComponentType<{ options: readonly T[]; selected: readonly T[] }>;

  /**
   * An optional CSS classes dictionary that will be applied to this element and its children.
   */
  classes?: CSSClasses<'root' | 'inputLabel' | 'select' | 'input'>;
}

interface NonValidatedMultiSelectProps {
  /**
   * The name of the input. Must be set to use validations.
   */
  name?: null;

  /**
   * The rules to validate this input with.
   */
  rules?: null;

  /**
   * Whether this input is required.
   */
  required?: false;
}

interface ValidatedMultiSelectProps<T> {
  /**
   * The name of the input. Must be set to use validations.
   */
  name: string;

  /**
   * The rules to validate this input with.
   */
  rules?: Validation<T[]>[];

  /**
   * Whether this input is required.
   */
  required: true;
}

export type MultiSelectProps<T> = Omit<
  SelectProps,
  | 'labelId'
  | 'label'
  | 'classes'
  | 'onBlur'
  | 'value'
  | 'open'
  | 'onOpen'
  | 'onClose'
  | 'onChange'
  | 'required'
  | 'error'
> &
  CommonMultiSelectProps<T> &
  (NonValidatedMultiSelectProps | ValidatedMultiSelectProps<T>);

export function MultiSelect<T extends SelectItemType>({
  sx,
  className,
  label,
  onChange,
  options,
  value,
  ItemComponent = DefaultItemRender,
  SelectComponent = DefaultSelectRender,
  name,
  rules,
  required,
  disabled,
  fullWidth,
  color,
  classes: propsClasses,
  MenuProps,
  ...props
}: MultiSelectProps<T>): ReactElement<MultiSelectProps<T>> {
  const [selectOpen, setSelectOpen] = useState(false);
  const { handleBlur, error, message, setValidating } = useInputValidation(name ?? 'multi-select', rules, value);
  const labelId = useId();

  const selectedRenderer = (ids: unknown): ReactNode => (
    <SelectComponent
      options={options}
      selected={
        Array.isArray(ids)
          ? (ids as string[]).map((id) => options.find((option) => getId(option) === id)).filter<T>(isDefined)
          : []
      }
    />
  );

  const handleChange: SelectProps['onChange'] = (event) => {
    onChange?.(options.filter((option) => (event.target.value as string[]).includes(getId(option))));
  };

  return (
    <FormControl
      variant="outlined"
      sx={sx}
      color={color}
      className={className}
      fullWidth={fullWidth}
      required={required}
      error={error}
    >
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <MuiSelect
        labelId={label && labelId}
        label={label}
        sx={{ height: (theme) => theme.spacing(5), fontSize: (theme) => theme.typography.body2 }}
        onBlur={handleBlur}
        value={value.map(getId)}
        renderValue={selectedRenderer}
        open={selectOpen}
        disabled={disabled}
        onOpen={() => {
          setSelectOpen(true);
          setValidating(false);
        }}
        onClose={() => {
          setSelectOpen(false);
          setValidating(true);
        }}
        onChange={handleChange}
        fullWidth={fullWidth}
        multiple
        color={color}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          ...MenuProps,
        }}
        {...props}
      >
        {options.map((option) => (
          <MenuItem key={getId(option)} value={getId(option)}>
            <ItemComponent value={option} selected={value.some((selected) => getId(selected) === getId(option))} />
          </MenuItem>
        ))}
      </MuiSelect>
      {message && <FormHelperText>{message}</FormHelperText>}
    </FormControl>
  );
}
